import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, Users, Lock } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { OfflineNotice } from "@/components/nds/windows/CampusResidentContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re/intake`;
const authHeader = () => {
  const t = localStorage.getItem("nex_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const errOf = (e) => {
  const d = e.response?.data?.detail;
  return typeof d === "object" && d && !Array.isArray(d) ? d : { message: Array.isArray(d) ? d.map((x) => x.msg || x).join(", ") : d || "Request failed" };
};
const optsOf = (f) => (f.options || []).map((o) => (typeof o === "object" ? o : { value: o, label: o }));
const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

const inputCls = "w-full px-3 py-2 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm rounded-sm";

function HousingPicker({ field, value, onChange }) {
  const v = value || {};
  const prop = (field.properties || []).find((p) => p.value === v.property);
  const room = prop?.rooms.find((r) => r.value === v.room);
  const sel = (tid, val, opts, empty, next) => (
    <select data-testid={tid} value={val || ""} onChange={(e) => next(e.target.value || null)} className={inputCls}>
      <option value="">{empty}</option>
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}{o.beds ? ` · ${o.beds.length} free` : ""}</option>)}
    </select>
  );
  return (
    <div className="grid sm:grid-cols-3 gap-2">
      {sel("intake-housing-property", v.property, field.properties || [], "No property yet", (p) => onChange({ property: p, room: null, bed: null }))}
      {prop && sel("intake-housing-room", v.room, prop.rooms, "Room (optional)", (r) => onChange({ ...v, room: r, bed: null }))}
      {room && sel("intake-housing-bed", v.bed, room.beds, room.beds.length ? "Free bed (optional)" : "No free beds", (b) => onChange({ ...v, bed: b }))}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const id = `intake-field-${field.key}`;
  const t = field.type || "text";
  if (t === "housing") return <HousingPicker field={field} value={value} onChange={onChange} />;
  if (t === "textarea") return <textarea data-testid={id} rows={3} value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  if (t === "select" || t === "radio") {
    return (
      <select data-testid={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">Select…</option>
        {optsOf(field).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (t === "multiselect" || (t === "checkbox" && field.options)) {
    const cur = Array.isArray(value) ? value : [];
    return (
      <div data-testid={id} className="flex flex-wrap gap-2">
        {optsOf(field).map((o) => {
          const on = cur.includes(o.value);
          return (
            <button key={o.value} type="button" data-testid={`${id}-${o.value}`} onClick={() => onChange(on ? cur.filter((v) => v !== o.value) : [...cur, o.value])}
              className={`px-3 py-1 rounded-full border text-xs transition-colors ${on ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (t === "boolean" || t === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 text-sm text-nex-silver">
        <input data-testid={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[#d4a017]" /> Yes
      </label>
    );
  }
  const htmlType = { phone: "tel", email: "email", date: "date", number: "number" }[t] || "text";
  return <input data-testid={id} type={htmlType} value={value ?? ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
}

function SectionStep({ section, answers, setAnswer, missing }) {
  return (
    <div className="space-y-4">
      {section.description && <p className="text-nex-muted text-sm">{section.description}</p>}
      {section.fields.map((f) => (
        <div key={f.key}>
          <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted mb-1.5">
            {f.label}{f.required && <span className="text-nex-gold"> *</span>}
            {f.sensitive && <Lock data-testid={`intake-sensitive-${f.key}`} className="inline h-3 w-3 ml-1.5 -mt-0.5 text-nex-gold" aria-label="Encrypted" />}
          </label>
          <FieldInput field={f} value={answers[f.key]} onChange={(v) => setAnswer(f.key, v)} />
          {f.help && <p className="mt-1 text-[11px] text-nex-muted">{f.help}</p>}
          {missing.includes(f.key) && <p data-testid={`intake-missing-${f.key}`} className="mt-1 text-[11px] text-red-300">Required</p>}
        </div>
      ))}
    </div>
  );
}

function Success({ resident, onView, onAnother }) {
  return (
    <div data-testid="intake-success" className="flex-1 flex items-center justify-center p-8 text-center">
      <div className="max-w-sm">
        <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
        <h3 className="text-nex-silver text-lg font-semibold">Intake complete</h3>
        <p className="text-nex-muted text-sm mt-1">
          {[resident.first_name, resident.last_name].filter(Boolean).join(" ") || "The resident"} was added to the Resident Engine and now appears in Campus Resident.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <button type="button" data-testid="intake-view-roster" onClick={onView} className="inline-flex items-center gap-2 px-4 py-2 bg-nex-gold/15 border border-nex-gold text-nex-gold font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm">
            <Users className="h-3.5 w-3.5" /> Open Campus Resident
          </button>
          <button type="button" data-testid="intake-new" onClick={onAnother} className="px-4 py-2 border border-white/10 text-nex-muted hover:text-nex-silver font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm">
            New intake
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ResidentIntakeContent({ win }) {
  const { openWindow, closeWindow } = useFloatingWindows();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [done, setDone] = useState(null);

  const defaultsOf = (f) => Object.fromEntries((f?.sections || []).flatMap((s) => s.fields)
    .filter((x) => x.default !== undefined).map((x) => [x.key, x.default]));

  useEffect(() => {
    setError(null); setForm(null);
    axios.get(`${API}/schema`, { headers: authHeader() })
      .then(({ data }) => { setForm(data); setAnswers(defaultsOf(data)); })
      .catch((e) => setError(errOf(e)));
  }, [attempt]);

  if (error) return <OfflineNotice error={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!form) return <div className="flex-1 flex items-center justify-center gap-2 text-nex-muted p-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading intake form...</div>;
  if (done) {
    return (
      <Success resident={done}
        onView={() => { openWindow("campus_residents", { title: "Campus Resident", description: "Browse the hoh2life resident roster — tap any resident for the full dossier." }); closeWindow(win.id); }}
        onAnother={() => { setDone(null); setAttempt((n) => n + 1); setStep(0); }} />
    );
  }

  const sections = (form.sections || []).filter((s) => s.fields?.length);
  const section = sections[step];
  const last = step === sections.length - 1;
  const setAnswer = (k, v) => { setAnswers((a) => ({ ...a, [k]: v })); setMissing((m) => m.filter((x) => x !== k)); };

  const next = async () => {
    const miss = section.fields.filter((f) => f.required && isEmpty(answers[f.key])).map((f) => f.key);
    if (miss.length) { setMissing(miss); return; }
    if (!last) { setStep(step + 1); return; }
    setSubmitting(true); setSubmitErr(null);
    try {
      const { data } = await axios.post(`${API}/submit`, { answers }, { headers: authHeader() });
      setDone(data.resident || data);
    } catch (e) {
      setSubmitErr(errOf(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="resident-intake-content" className="flex flex-col h-full" style={{ minHeight: "480px" }}>
      <div className="px-5 pt-4 pb-3 border-b border-white/10">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold">Step {step + 1} of {sections.length}</div>
        <h3 data-testid="intake-section-title" className="text-nex-silver text-lg font-semibold">{section.title}</h3>
        <div className="mt-2 h-1 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-nex-gold transition-[width] duration-300" style={{ width: `${((step + 1) / sections.length) * 100}%` }} />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        <SectionStep section={section} answers={answers} setAnswer={setAnswer} missing={missing} />
      </div>
      <div className="px-5 py-3 border-t border-white/10 flex items-center gap-3">
        <button type="button" data-testid="intake-back" disabled={step === 0} onClick={() => setStep(step - 1)}
          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted hover:text-nex-silver disabled:opacity-30">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        {submitErr && <span data-testid="intake-submit-error" className="text-xs text-red-300 truncate">{submitErr}</span>}
        <button type="button" data-testid="intake-next" onClick={next} disabled={submitting}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-nex-gold/15 border border-nex-gold text-nex-gold font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {last ? "Complete intake" : "Next"} {!last && <ArrowRight className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
